import type { Router, RouteRecordNormalized } from 'vue-router'

/**
 * Name and parent lookups over the router's static route table.
 *
 * `router.getRoutes()` returns flat records with no parent pointer, so anything that needs a
 * route's ancestry (access checks, the "Attendance / Time Cards / Editor" trail) has to rebuild
 * it. The console never adds or removes routes at runtime, so the tree is built once per router.
 */
export interface RouteTree {
  /** Canonical records by name. Alias copies (`aliasOf`) are left out -- they share the name. */
  byName: Map<string, RouteRecordNormalized>
  parentOf: (_record: RouteRecordNormalized) => RouteRecordNormalized | undefined
  /** The record followed by each ancestor, innermost first. */
  ancestry: (_record: RouteRecordNormalized) => RouteRecordNormalized[]
}

const trees = new WeakMap<Router, RouteTree>()

export function getRouteTree (router: Router): RouteTree {
  const cached = trees.get(router)
  if (cached) {
    return cached
  }

  const byName = new Map<string, RouteRecordNormalized>()
  const parentByChildName = new Map<string, RouteRecordNormalized>()

  for (const record of router.getRoutes()) {
    if (record.aliasOf) {
      continue
    }
    if (typeof record.name === 'string') {
      byName.set(record.name, record)
    }
    for (const child of record.children ?? []) {
      if (typeof child.name === 'string') {
        parentByChildName.set(child.name, record)
      }
    }
  }

  const parentOf = (record: RouteRecordNormalized) =>
    typeof record.name === 'string' ? parentByChildName.get(record.name) : undefined

  const ancestry = (record: RouteRecordNormalized) => {
    const chain: RouteRecordNormalized[] = []
    for (let current: RouteRecordNormalized | undefined = record; current; current = parentOf(current)) {
      chain.push(current)
    }
    return chain
  }

  const tree = { byName, parentOf, ancestry }
  trees.set(router, tree)
  return tree
}
